import React from "react";
import { format } from "date-fns";
import cx from "classnames";

import InfiniteScroll from "src/components/infiniteScroll";
import Text from "src/components/text";

import { CustomFormField, FormFieldType } from "src/types/settings/customFormFields";

import styles from "./styles.module.scss";

interface MenuPaneProps {
  fields: CustomFormField[];
  selectedFieldId: string | null;
  onSelectField: (field: CustomFormField) => void;
  hasMore: boolean;
  isLoading: boolean;
  loadMore: () => void;
}

const FIELD_TYPE_ICONS: Record<FormFieldType, string> = {
  list_field: "paperflite-list",
  string_field: "paperflite-text-field",
  number_field: "paperflite-number-field",
  boolean_field: "fa-regular fa-circle-check",
};

const FIELD_TYPE_LABELS: Record<FormFieldType, string> = {
  list_field: "List",
  string_field: "Text",
  number_field: "Number",
  boolean_field: "Decision",
};

const FieldTypeIcon: React.FC<{ type: FormFieldType; active: boolean }> = ({ type, active }) => {
  if (type === "number_field") {
    return (
      <span className={cx(styles.typeIconText, { [styles.activeIcon]: active })}>#</span>
    );
  }
  if (type === "string_field") {
    return (
      <span className={cx(styles.typeIconText, { [styles.activeIcon]: active })}>Tx</span>
    );
  }
  return (
    <i className={cx(styles.typeIcon, FIELD_TYPE_ICONS[type], { [styles.activeIcon]: active })} />
  );
};

const FieldItem: React.FC<{
  field: CustomFormField;
  isSelected: boolean;
  onClick: () => void;
}> = ({ field, isSelected, onClick }) => {
  const formatDate = (date: number | string) => {
    try {
      const d = new Date(date);
      const now = new Date();
      const diffMs = now.getTime() - d.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return "just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      return format(d, "do MMM yyyy");
    } catch {
      return "—";
    }
  };

  return (
    <div
      className={cx(styles.fieldItem, { [styles.selected]: isSelected })}
      onClick={onClick}
    >
      <div className={styles.iconWrapper}>
        <FieldTypeIcon type={field.type} active={isSelected} />
      </div>
      <div className={styles.info}>
        <div className={cx(styles.name, { [styles.selectedName]: isSelected })}>
          <Text text={field.name} lines={1} tooltip={{ placement: "auto" }} />
        </div>
        <div className={styles.meta}>last updated: {formatDate(field.modifiedDate)}</div>
      </div>
      {isSelected && <div className={styles.selectedLine} />}
    </div>
  );
};

const MenuPane: React.FC<MenuPaneProps> = ({
  fields,
  selectedFieldId,
  onSelectField,
  hasMore,
  isLoading,
  loadMore,
}) => {
  return (
    <div className={styles.menuPane}>
      <InfiniteScroll
        hasMore={hasMore}
        loading={isLoading}
        loadMore={loadMore}
        className={styles.scrollBody}
      >
        {fields.map((field) => (
          <FieldItem
            key={field.id}
            field={field}
            isSelected={field.id === selectedFieldId}
            onClick={() => onSelectField(field)}
          />
        ))}
      </InfiniteScroll>
    </div>
  );
};

export default MenuPane;
